import { ReactElement } from 'react';
import {
    ResponsiveContainer,
    Tooltip,
    PieChart,
    Pie,
    Legend,
    TooltipContentProps
} from 'recharts';
import { ChartContainer } from './chart-container';
import { moonlight } from '@/ui/styles';
import { UIText } from '@/ui';
import { useAnalyticsTranslation } from '../hooks';
import { useTasksAndAlertsAnalytics } from '../providers/tasks-and-alerts-analytics-provider';

function CustomTooltip(
    props: TooltipContentProps<number, string>
): ReactElement {
    const { active, payload } = props;
    if (!active || !payload || payload.length === 0) {
        return <></>;
    }

    type ChartData = {
        fill: string;
        label: string;
        value: number;
    };
    const data = (payload[0] as { payload: ChartData }).payload;
    const fill = data.fill || '';
    const label = data.label || '';
    const value = data.value || 0;

    return <TooltipContent fill={fill} label={label} value={value} />;
}

function TooltipContent(props: {
    fill: string;
    label: string;
    value: number;
}): ReactElement {
    const { fill, label, value } = props;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                borderRadius: 8,
                border: `1px solid ${moonlight}`,
                gap: 8,
                padding: 8
            }}
        >
            <div
                style={{
                    display: 'flex',

                    alignItems: 'center',
                    gap: 8
                }}
            >
                <div
                    style={{
                        width: 12,
                        height: 12,
                        borderRadius: 12,
                        backgroundColor: fill
                    }}
                />
                <UIText>{`${label}: ${value}`}</UIText>
            </div>
        </div>
    );
}

export function RealVsFalseAlertsPieChart(): ReactElement {
    const { t } = useAnalyticsTranslation();
    const { realVsFalseAlertData, status, dateString } =
        useTasksAndAlertsAnalytics();

    return (
        <ChartContainer
            title={t('components.real_vs_false_alerts_pie_chart.title')}
            titleWeight="medium"
            subtitle={dateString || '...'}
            description={t(
                'components.real_vs_false_alerts_pie_chart.description'
            )}
            loading={status === 'pending'}
            error={status === 'error'}
        >
            <ResponsiveContainer width="100%" height="100%">
                <PieChart data={realVsFalseAlertData} width={200} height={200}>
                    <Pie data={realVsFalseAlertData} dataKey="value" />
                    <Tooltip content={CustomTooltip} />
                    <Legend
                        verticalAlign="middle"
                        layout="vertical"
                        align="right"
                        iconType="circle"
                        formatter={(_, { payload }) => {
                            const label =
                                (payload as unknown as { label: string })
                                    .label || '';
                            return (
                                <UIText type="default2" variant="small">
                                    {label}
                                </UIText>
                            );
                        }}
                    />
                </PieChart>
            </ResponsiveContainer>
        </ChartContainer>
    );
}
